import React from 'react';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Toolbar from '@material-ui/core/Toolbar';
import {
  VseCourseEditGrid,
  CourseErrorKeyResolver,
} from './VseCourseEditGrid';
import { VseCourseHeaderGrid } from './VseCourseHeaderGrid';
import {
  VseClassEditList,
  ClassErrorKeyResolver,
  ClassComponentErrorKeyResolver,
  ClassComponentScheduleErrorKeyResolver,
} from './VseClassEditList';
import { VseCourseEditTemplate } from '../types/VseCourseEditTemplate';
import { VseClassEditTemplate, VseClassComponentEditTemplate, VseClassComponentScheduleEditTemplate } from '../types/VseClassEditTemplate';
import {
  SeasonEntity,
  UniversityEntity,
  CourseStatusEntity,
  LanguageEntity,
  UniversityTermEntity,
  TeachingModeEntity,
  TimeZoneEntity,
  DotNetValidationProblemDetails,
} from '../types';
import { VseCourseEditMode } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  form: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  selectedTab: {
    color: theme.palette.common.white,
  },
}));

export interface VseCourseEditFormProps {
  courseData: VseCourseEditTemplate,
  seasonOptions: SeasonEntity[],
  universityOptions: UniversityEntity[],
  universityTermOptions: UniversityTermEntity[],
  courseStatusOptions: CourseStatusEntity[],
  languageOptions: LanguageEntity[],
  teachingModeOptions: TeachingModeEntity[],
  timeZoneOptions: TimeZoneEntity[],
  readOnly?: boolean,
  mode?: VseCourseEditMode,
  onSubmit: () => void,
  onSave: () => void,
  onBack: () => void,
  onCourseChange: (changes: Partial<VseCourseEditTemplate>) => void,
  onClassChange: (index: number, changes: Partial<VseClassEditTemplate>) => void,
  onClassAdd: () => void,
  onClassDelete: (index: number) => void,
  onComponentChange: (index: number, compIndex: number, changes: Partial<VseClassComponentEditTemplate>) => void,
  onComponentAdd: (index: number) => void,
  onComponentDelete: (index: number, compIndex: number) => void,
  onScheduleChange: (index: number, compIndex: number, schdIndex: number, changes: Partial<VseClassComponentScheduleEditTemplate>) => void,
  onScheduleAdd: (index: number, compIndex: number) => void,
  onScheduleDelete: (index: number, compIndex: number, schdIndex: number) => void,
  resolveCourseErrorKey: CourseErrorKeyResolver,
  resolveClassErrorKey: ClassErrorKeyResolver,
  resolveClassComponentErrorKey: ClassComponentErrorKeyResolver,
  resolveClassComponentScheduleErrorKey: ClassComponentScheduleErrorKeyResolver,
  valProblems?: DotNetValidationProblemDetails,
}

export function VseCourseEditForm(props: VseCourseEditFormProps) {
  function decodeSeason(seasonID: number) {
    const data = props.seasonOptions.filter((e) => e.seasonID === seasonID);
    if (data && data.length > 0) return data[0].shortDescription;
    return undefined;
  }
  function decodeUniversity(univID: number) {
    const data = props.universityOptions.filter((e) => e.universityID === univID);
    if (data && data.length > 0) return data[0].universityName;
    return undefined;
  }
  function decodeUniversityTerm(seasonID: number, universityID: number, termCode: string) {
    const data = props.universityTermOptions.filter((e) => e.seasonID === seasonID && e.universityID === universityID && e.termCode === termCode);
    if (data && data.length > 0) return data[0].description;
    return undefined;
  }
  function decodeCourseStatus(code: string) {
    const data = props.courseStatusOptions.filter((e) => e.code === code);
    if (data && data.length > 0) return data[0].description;
    return undefined;
  }

  const theme = useTheme();
  const classes = useStyles(theme);
  const [tab, setTab] = React.useState<'course' | 'class'>('course');
  const valProblemKeys = props.valProblems ? Object.keys(props.valProblems.errors) : undefined;
  const {
    mode = 'partnerUniv',
  } = props;

  React.useLayoutEffect(() => {
    if (props.valProblems) {
      const keys = Object.keys(props.valProblems.errors);
      let courseError = false;
      let classError = false;
      keys.forEach((value) => {
        if (value.startsWith('course')) {
          courseError = true;
        } else if (value.startsWith('classes')) {
          classError = true;
        }
      });
      if (courseError) {
        setTab('course');
      } else if (classError) {
        setTab('class');
      }
    }
  }, [props.valProblems]);

  const isDraft = props.courseData.statusCode === 'DR';
  const showSubmitButton: boolean = !props.readOnly && props.mode === 'partnerUniv' && isDraft;
  const showSaveButton: boolean = !props.readOnly && ((props.mode === 'centralOffice') || showSubmitButton);

  return (
    <React.Fragment>
      <AppBar
        className={classes.appBar}
        position='static'
        color='inherit'
        elevation={0}
      >
        <Toolbar>
          <ButtonGroup variant="contained">
            {showSubmitButton && (
              <Button
                color="primary"
                onClick={() => props.onSubmit()}
              >
                Submit
              </Button>
            )}
            {showSaveButton && (
              <Button
                color="secondary"
                onClick={() => props.onSave()}
              >
                Save
              </Button>
            )}
            <Button
              color="inherit"
              onClick={() => props.onBack()}
            >
              Back
            </Button>
          </ButtonGroup>
        </Toolbar>
      </AppBar>
      <Paper>
        <AppBar
          className={classes.appBar}
          position='relative'
          color='secondary'
        >
          <Toolbar className={classes.toolbar}>
            <Tabs
              value={tab}
              onChange={(_, newValue) => setTab(newValue)}
            >
              <Tab
                classes={{
                  selected: classes.selectedTab
                }}
                value='course'
                label='Course Information'
              />
              <Tab
                classes={{
                  selected: classes.selectedTab
                }}
                value='class'
                label='Class Schedule'
              />
            </Tabs>
          </Toolbar>
        </AppBar>

        <form className={classes.form}>
          <div hidden={tab !== 'course'}>
            <VseCourseEditGrid
              data={props.courseData}
              mode={mode}
              readOnly={props.readOnly}
              decodeSeason={decodeSeason}
              decodeUniversity={decodeUniversity}
              decodeUniversityTerm={decodeUniversityTerm}
              courseStatusOptions={props.courseStatusOptions}
              languageOptions={props.languageOptions}
              onChange={props.onCourseChange}
              resolveCourseErrorKey={props.resolveCourseErrorKey}
              valProblemKeys={valProblemKeys}
            />
          </div>
          <div hidden={tab !== 'class'}>
            <VseCourseHeaderGrid
              data={props.courseData}
              decodeSeason={decodeSeason}
              decodeUniversity={decodeUniversity}
              decodeUniversityTerm={decodeUniversityTerm}
              decodeCourseStatus={decodeCourseStatus}
            />
            <VseClassEditList
              list={props.courseData.classes}
              readOnly={props.readOnly}
              teachingModeOptions={props.teachingModeOptions}
              timeZoneOptions={props.timeZoneOptions}
              onChange={props.onClassChange}
              onAdd={props.onClassAdd}
              onDelete={props.onClassDelete}
              onComponentChange={props.onComponentChange}
              onComponentAdd={props.onComponentAdd}
              onComponentDelete={props.onComponentDelete}
              onScheduleChange={props.onScheduleChange}
              onScheduleAdd={props.onScheduleAdd}
              onScheduleDelete={props.onScheduleDelete}
              resolveClassErrorKey={props.resolveClassErrorKey}
              resolveClassComponentErrorKey={props.resolveClassComponentErrorKey}
              resolveClassComponentScheduleErrorKey={props.resolveClassComponentScheduleErrorKey}
              valProblemKeys={valProblemKeys}
            />
          </div>
        </form>
      </Paper>
    </React.Fragment>
  );
}
